/**
 * Diagnostics for the two ways a google map fails without saying anything.
 *
 * The loader resolves only from google's own callback and rejects only from script.onerror, and tilesLoaded
 * waits on a 'tilesloaded' event with no timeout. A script that returns 200 but refuses to initialise - a
 * rejected key or client id does exactly that - trips neither path, so the page sits on "Loading map..."
 * with a clean console and nothing in the logs.
 *
 * Plain functions rather than an injectable service: this is instrumentation, and keeping it out of the
 * providers means the map pages can use it without a module change.
 */

const MAP_READY_TIMEOUT_MS = 20000;

export interface IMapDiagnosticsLogger {
    error(message: string): void;
    exception(message: string, context: any): void;
}

interface IMapDiagnostics {
    /** The url google maps was actually asked for - the fastest way to tell a key from a stale client id. */
    scriptSrc: string;
    /** False means the script never finished, rather than the map failing later. */
    googleMapsLoaded: boolean;
    /** A zero-height canvas makes google skip rendering, which also leaves tilesloaded unfired. */
    canvasSize: string;
}

const collect = (): IMapDiagnostics => {
    const script = document.querySelector('script[src*="maps.google"]') as HTMLScriptElement;
    // whichever of the two map components is on screen; the full map and the dashboard's simple map use
    // different canvas classes
    const canvas = document.querySelector(
        '.apto-map--googlemap-canvas, .apto-simple-map--googlemap-canvas'
    ) as HTMLElement;
    const google = (window as any).google;

    return {
        scriptSrc: script ? script.src : '(no google maps script in the document)',
        googleMapsLoaded: !!(google && google.maps),
        canvasSize: canvas ? `${canvas.offsetWidth}x${canvas.offsetHeight}` : '(canvas not in the document)'
    };
};

/**
 * Routes google's own authentication failures into our logs. Google calls this global on any credential
 * problem - InvalidClientIdMapError, RefererNotAllowedMapError and the rest - but only writes to the browser
 * console otherwise, so without it those failures never reach the server.
 *
 * Idempotent, because both map pages install it and whichever loads first wins.
 */
export const installGoogleMapsAuthFailureHook = (logger: IMapDiagnosticsLogger): void => {
    const globalWindow = window as any;

    if (globalWindow.gm_authFailure) {
        return;
    }

    globalWindow.gm_authFailure = () => {
        logger.exception('Google Maps rejected our credentials', collect());
    };
};

/**
 * Logs why a map is still not ready after a generous wait. Returns the cancel function - call it from the
 * map ready handler, otherwise a healthy map reports itself as broken.
 */
export const watchForMapReady = (logger: IMapDiagnosticsLogger, label: string): (() => void) => {
    const timer = setTimeout(
        () => logger.exception(`${label}: map still not ready after ${MAP_READY_TIMEOUT_MS}ms`, collect()),
        MAP_READY_TIMEOUT_MS
    );

    return () => clearTimeout(timer);
};
